"use client";
import React from "react";
import { Image } from "components/common";
import AppQRCode from "components/common/AppQRCode";
import AppStoreButtons from "components/common/AppStoreButtons";
import { useT } from "providers/I18nProvider";
import useStoreUrl from "hooks/useStoreUrl";
import { Gift } from "lucide-react";
const SectionDownloadApp = ({
  variant = "full",
}: {
  /** "full" = dải ngang cuối trang (mặc định) · "sidebar" = card gọn cho cột phải trang shop
   *  · "inline" = 1 dòng gọn + nút Tải app (trang shop mobile, dưới Ưu đãi) */
  variant?: "full" | "sidebar" | "inline";
}) => {
  const t = useT();
  const storeUrl = useStoreUrl();

  if (variant === "inline") {
    return (
      <a
        href={storeUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="mb-4 flex items-center gap-3 rounded-[14px] bg-gradient-to-br from-[#dff6f5] to-[#eefbfb] px-3.5 py-3"
      >
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary text-white">
          <Gift width={22} height={22} strokeWidth={2} aria-hidden="true" />
        </span>
        <span className="min-w-0 flex-1">
          <b className="block text-[14px] text-black01">{t("downloadApp.mobileTitle")}</b>
          <span className="block text-[12px] text-gray-600">{t("downloadApp.mobileDesc")}</span>
        </span>
        <span className="shrink-0 rounded-full bg-primary px-3 py-2 text-[12px] font-bold text-white">
          {t("downloadApp.mobileBtn")}
        </span>
      </a>
    );
  }

  if (variant === "sidebar") {
    return (
      <section className="rounded-2xl bg-primary/10 p-4">
        <h3 className="!mb-2 text-[15px] font-extrabold text-primary">
          {t("downloadApp.sidebarTitle")}
        </h3>
        <p className="!mb-3 text-[13px] text-gray-700">
          {t("downloadApp.sidebarDesc")}
        </p>
        {/* Mobile: nút mở store thay cho QR */}
        <AppStoreButtons size="md" className="md:hidden justify-start" />
        <div className="hidden md:flex items-center gap-3">
          <AppQRCode sizeClassName="w-20 h-20 shrink-0" />
          <p className="!mb-0 text-xs text-gray-600">
            {t("downloadApp.sidebarScan")}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section
      className="relative flex items-end overflow-hidden bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: 'url("/bg-color.webp")' }}
    >
      <div className="container mx-auto px-4 lg:px-6">
        <div className="flex justify-center mx-auto">
          {/* Images */}
          <div className="hidden lg:flex w-[300px] shrink-0 items-end justify-end pt-4">
            <Image
              isRelative={true}
              src="/footer-image.webp"
              alt={t("downloadApp.phoneAlt")}
              wrapClassName="w-full h-auto max-w-[300px] aspect-[1058/900] object-contain hidden lg:flex"
              sizes="300px"
            />
          </div>
          {/* Content */}
          <div className="flex justify-center items-center lg:items-start flex-col pl-0 lg:pl-8 py-3 md:py-4 lg:py-6">
            <div className="flex mb-2.5 md:mb-3">
              {/* Mobile: 1 khối chữ chảy tự nhiên, căn giữa (trước đây flex 2 cột → "Ưu đãi" bị kẹp dọc) */}
              <h2 className="text-[17px] md:text-[20px] lg:text-[22px] leading-snug font-bold text-gray-800 flex flex-col items-center lg:items-start gap-0.5 text-center lg:text-left">
                <span className="block md:flex md:gap-2">
                  <span className="text-[#ff914d]">{t("downloadApp.offer")}</span>{" "}
                  <span className="text-[#1f9fa6]">
                    {t("downloadApp.services")}
                  </span>
                </span>
                <span className="text-[#1f9fa6]">
                  {t("downloadApp.onlyOnApp")}{" "}
                </span>
              </h2>
            </div>

            {/* Main Content Grid */}
            <div className="flex flex-col gap-2.5 md:gap-3">
              {/* Mobile: bỏ dòng phụ (banner đã rõ ý, tab Tải App luôn ở đáy) */}
              <p className="hidden md:block text-[15px] text-center md:text-left md:text-[15px] text-gray-600 mb-0">
                {t("downloadApp.downloadNow")}
              </p>
              <div className="flex flex-col md:flex-row gap-4 md:gap-5 items-center">
                {/* Left Side - QR Code */}
                {/* QR chỉ có ý nghĩa khi xem trên máy tính → ẩn trên mobile */}
                <div className="hidden md:flex flex-col items-center justify-center space-y-4">
                  <AppQRCode sizeClassName="w-24 h-24" />
                </div>
                {/* Mobile: 2 nút cỡ md nằm cạnh nhau (150px×2 vừa 360px) */}
                <AppStoreButtons size="md" className="md:hidden flex-row justify-center gap-3" />
                <AppStoreButtons
                  size="md"
                  className="hidden md:flex flex-row gap-3"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SectionDownloadApp;
